import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, CheckCircle2, ExternalLink, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploader } from "@/components/ImageUploader";

export default function VendorDashboard() {
  const { L } = useLang();
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/vendors/mine").then((r) => setV(r.data)).catch(() => setV(false));
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  if (v === false) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk">No vendor profile.</div>;
  if (!v) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk animate-pulse">Loading…</div>;

  const set = (k, val) => setV({ ...v, [k]: val });
  const setSocial = (k, val) => setV({ ...v, social: { ...(v.social || {}), [k]: val } });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/vendors/mine", v);
      setV(data);
      toast.success(L("已保存", "Saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const field = "fk-border rounded-lg mt-1";
  const pending = v.status === "pending";
  const rejected = v.status === "rejected";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-4xl">摊主后台 / Vendor</h1>
        {v.status === "approved" && (
          <Link to={`/vendors/${v.slug}`} className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#00E5FF] fk-lift flex items-center gap-1">
            <ExternalLink size={15} /> {L("查看专页", "View page")}
          </Link>
        )}
      </div>

      <div className={`mt-4 fk-border rounded-xl p-4 flex items-center gap-2 font-medium ${pending ? "bg-[#FFD93D]" : rejected ? "bg-[#FF5757] text-white" : "bg-[#00C853] text-white"}`} data-testid="vendor-status">
        {v.status === "approved" ? <CheckCircle2 size={18} /> : <Clock size={18} />}
        {pending && L("审核中：管理员批准后你的专页将公开显示。", "Pending: your page goes live once an admin approves.")}
        {rejected && L("申请未通过，请联络管理员。", "Application rejected. Please contact admin.")}
        {v.status === "approved" && L("已批准：专页已公开！", "Approved: your page is live!")}
      </div>

      <div className="mt-6 bg-white fk-border-thick rounded-2xl p-6 sm:p-8 fk-shadow space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">{L("摊位名称（中）", "Name (ZH)")}</label><Input value={v.name_zh || ""} onChange={(e) => set("name_zh", e.target.value)} data-testid="vd-name-zh" className={field} /></div>
          <div><label className="text-sm font-medium">{L("摊位名称（英）", "Name (EN)")}</label><Input value={v.name_en || ""} onChange={(e) => set("name_en", e.target.value)} data-testid="vd-name-en" className={field} /></div>
        </div>
        <div><label className="text-sm font-medium">{L("类别", "Category")}</label>
          <Select value={v.category || ""} onValueChange={(val) => set("category", val)}>
            <SelectTrigger className={field} data-testid="vd-category"><SelectValue placeholder={L("选择类别", "Select category")} /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.name_zh}>{L(c.name_zh, c.name_en)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><label className="text-sm font-medium">{L("简介（中）", "Bio (ZH)")}</label><Textarea rows={3} value={v.bio_zh || ""} onChange={(e) => set("bio_zh", e.target.value)} data-testid="vd-bio-zh" className={field} /></div>
        <div><label className="text-sm font-medium">{L("简介（英）", "Bio (EN)")}</label><Textarea rows={3} value={v.bio_en || ""} onChange={(e) => set("bio_en", e.target.value)} className={field} /></div>

        <div>
          <label className="text-sm font-medium">{L("照片（可多张）", "Photos (multiple)")}</label>
          <div className="mt-2"><ImageUploader value={v.photos || []} onChange={(val) => set("photos", val)} /></div>
        </div>
        <div>
          <label className="text-sm font-medium">{L("影片（可多段）", "Videos (multiple)")}</label>
          <div className="mt-2"><ImageUploader value={v.videos && v.videos.length ? v.videos : (v.video_url ? [v.video_url] : [])} onChange={(val) => set("videos", val)} accept="video/*" label={L("上传影片", "Upload video")} /></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">{L("联络电话", "Phone")}</label><Input value={v.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={field} /></div>
          <div><label className="text-sm font-medium">WhatsApp</label><Input placeholder="60123456789" value={v.social?.whatsapp || ""} onChange={(e) => setSocial("whatsapp", e.target.value)} className={field} /></div>
          <div><label className="text-sm font-medium">Instagram URL</label><Input value={v.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} className={field} /></div>
          <div><label className="text-sm font-medium">Facebook URL</label><Input value={v.social?.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} className={field} /></div>
        </div>

        <div><label className="text-sm font-medium">{L("最新市集资讯（中）", "Latest market info (ZH)")}</label><Textarea rows={2} value={v.latest_market_zh || ""} onChange={(e) => set("latest_market_zh", e.target.value)} className={field} /></div>
        <div><label className="text-sm font-medium">{L("最新市集资讯（英）", "Latest market info (EN)")}</label><Textarea rows={2} value={v.latest_market_en || ""} onChange={(e) => set("latest_market_en", e.target.value)} className={field} /></div>

        <button onClick={save} disabled={saving} data-testid="vd-save" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#FFD93D] fk-lift disabled:opacity-50 flex items-center justify-center gap-2">
          <Save size={16} /> {saving ? "…" : L("保存修改", "Save changes")}
        </button>
      </div>
    </div>
  );
}
